import React from 'react';
import { makeStyles } from "@material-ui/core/styles";
import { getStartEndDate } from '../../utils';

const useStyles = makeStyles({
    formContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '3px',
        padding: '16px',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    },
    fieldContainer: {
        flex: '1 1 48%',
        minWidth: '200px',
    },
    label: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#3E83FA',
        marginBottom: '8px',
        display: 'block',
    },
    input: {
        width: '50%',
        padding: '8px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '14px',
    },
    select: {
        width: '50%',
        padding: '8px',
        border: '1px solid #ccc',
        borderRadius: '4px',
        fontSize: '14px',
        backgroundColor: '#fff',
    },
    errorText: {
        color: '#FF0000',
        fontSize: '12px',
        marginTop: '4px',
    },
    fieldError: {
        color: '#FF0000',
        fontSize: '11px',
        marginTop: '2px',
    },
    // Bands control
    bandsWrap: {
        border: '1px solid #E5E7EB',
        borderRadius: 8,
        padding: 12,
        background: '#FAFCFF',
        maxWidth: 520,
    },
    bandsModeRow: {
        display: 'flex',
        gap: 16,
        marginBottom: 10,
        fontSize: 13,
        color: '#374151',
    },
    rangesList: { display: 'flex', flexDirection: 'column', gap: 8 },
    rangeRow: { display: 'flex', gap: 8, alignItems: 'center' },
    rangeInput: {
        width: 100,
        padding: '6px 8px',
        border: '1px solid #ccc',
        borderRadius: 4,
        fontSize: 13,
    },
    rangeSep: { color: '#8892a4', fontSize: 12 },
    removeBtn: {
        background: 'transparent',
        border: 'none',
        color: '#DC2626',
        cursor: 'pointer',
        fontSize: 12,
        padding: '4px 8px',
    },
    addBtn: {
        marginTop: 6,
        background: '#ECF3FF',
        color: '#3E83FB',
        border: '1px solid #C5DAFE',
        borderRadius: 6,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 600,
        padding: '6px 12px',
    },
    hint: {
        fontSize: 11,
        color: '#8892a4',
        marginTop: 6,
    },
    bucketRow: { display: 'flex', gap: 8, alignItems: 'center' },
    bucketInput: {
        width: 80,
        padding: '6px 8px',
        border: '1px solid #ccc',
        borderRadius: 4,
        fontSize: 13,
    },
});

// Bands editor — either explicit ranges (repeatable [from, to] pairs; -1 =
// open-ended max) or a bucket count that asks the server to auto-generate N
// equal-width bands. Serializes into a shape the API/service layer flattens:
//   { mode: "ranges", ranges: ["0,2000", "2001,-1"] }
//   { mode: "buckets", buckets: 5 }
//   { mode: "default" } → no bands params sent, server picks defaults
function BandsControl({ value, onChange, classes, fieldErrors }) {
    const bands = value || { mode: "default", ranges: [{ from: "", to: "" }], buckets: 5 };

    const setMode = (mode) => onChange({ ...bands, mode });
    const setRange = (i, patch) => {
        const next = bands.ranges.map((r, idx) => (idx === i ? { ...r, ...patch } : r));
        onChange({ ...bands, ranges: next });
    };
    const addRange = () => onChange({ ...bands, ranges: [...(bands.ranges || []), { from: "", to: "" }] });
    const removeRange = (i) => onChange({ ...bands, ranges: bands.ranges.filter((_, idx) => idx !== i) });

    return (
        <div className={classes.bandsWrap}>
            <div className={classes.bandsModeRow}>
                <label>
                    <input
                        type="radio"
                        checked={bands.mode === "default"}
                        onChange={() => setMode("default")}
                    />{" "}
                    Default bands
                </label>
                <label>
                    <input
                        type="radio"
                        checked={bands.mode === "buckets"}
                        onChange={() => setMode("buckets")}
                    />{" "}
                    Auto (bucket count)
                </label>
                <label>
                    <input
                        type="radio"
                        checked={bands.mode === "ranges"}
                        onChange={() => setMode("ranges")}
                    />{" "}
                    Custom ranges
                </label>
            </div>

            {bands.mode === "buckets" && (
                <div className={classes.bucketRow}>
                    <span style={{ fontSize: 13, color: "#374151" }}>Number of buckets:</span>
                    <input
                        type="number"
                        min={1}
                        max={20}
                        value={bands.buckets}
                        onChange={(e) => onChange({ ...bands, buckets: Number(e.target.value) })}
                        className={classes.bucketInput}
                    />
                    <span className={classes.hint}>Server splits 0..max into N equal-width bands.</span>
                </div>
            )}

            {bands.mode === "ranges" && (
                <div className={classes.rangesList}>
                    {(bands.ranges || []).map((r, i) => (
                        <div key={i} className={classes.rangeRow}>
                            <input
                                type="number"
                                placeholder="From"
                                value={r.from}
                                onChange={(e) => setRange(i, { from: e.target.value })}
                                className={classes.rangeInput}
                            />
                            <span className={classes.rangeSep}>—</span>
                            <input
                                type="number"
                                placeholder="To (-1 = open)"
                                value={r.to}
                                onChange={(e) => setRange(i, { to: e.target.value })}
                                className={classes.rangeInput}
                            />
                            {(bands.ranges || []).length > 1 ? (
                                <button type="button" onClick={() => removeRange(i)} className={classes.removeBtn}>
                                    Remove
                                </button>
                            ) : null}
                        </div>
                    ))}
                    <button type="button" onClick={addRange} className={classes.addBtn}>
                        + Add band
                    </button>
                    <div className={classes.hint}>
                        Use -1 as the upper bound of the last band for "open-ended to the largest transaction".
                    </div>
                </div>
            )}
            {fieldErrors?.ranges ? (
                <div className={classes.fieldError}>{fieldErrors.ranges.join(" · ")}</div>
            ) : null}
            {fieldErrors?.buckets ? (
                <div className={classes.fieldError}>{fieldErrors.buckets.join(" · ")}</div>
            ) : null}
        </div>
    );
}

// Given the internal `bands` object, produce the query-param subset the
// service layer needs. Exposed so the parent can flatten right before
// dispatch — the reducer doesn't need to know about the two modes.
export const bandsToParams = (bands) => {
    if (!bands || bands.mode === "default") return {};
    if (bands.mode === "buckets") {
        return { buckets: bands.buckets && bands.buckets > 0 ? bands.buckets : 5 };
    }
    const ranges = (bands.ranges || [])
        .map((r) => {
            const from = r.from === "" || r.from === null || r.from === undefined ? null : Number(r.from);
            const to = r.to === "" || r.to === null || r.to === undefined ? null : Number(r.to);
            if (from === null || to === null) return null;
            if (Number.isNaN(from) || Number.isNaN(to)) return null;
            return `${from},${to}`;
        })
        .filter(Boolean);
    return ranges.length ? { ranges } : {};
};

const FilterForm = ({ filters, setFilters, filterConfig, validationError, fieldErrors }) => {
    const classes = useStyles();

    const handleFilterChange = (name, value) => {
        setFilters({
            ...filters,
            [name]: value,
        });
    };
    const [start_date, end_date] = getStartEndDate();

    return (
        <div className={classes.formContainer}>
            {filterConfig?.map((filter) => (
                <div key={filter.name} className={classes.fieldContainer}>
                    <label htmlFor={filter.name} className={classes.label}>
                        {filter.label}
                    </label>
                    {filter.type === 'date' ? (
                        <input
                            type="date"
                            id={filter.name}
                            value={filters?.[filter.name] ? filters[filter.name] : (
                                filter.name === "start_date" ? start_date.toISOString().split("T")[0] : end_date.toISOString().split("T")[0]
                            )}
                            onChange={(e) => handleFilterChange(filter.name, e.target.value)}
                            className={classes.input}
                        />
                    ) : filter.type === 'select' ? (
                        <select
                            id={filter.name}
                            value={filters?.[filter.name] || ''}
                            onChange={(e) => handleFilterChange(filter.name, e.target.value)}
                            className={classes.select}
                        >
                            <option value="">Select {filter.label}</option>
                            {filter.options?.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    ) : filter.type === 'bands' ? (
                        <BandsControl
                            value={filters?.[filter.name]}
                            onChange={(next) => handleFilterChange(filter.name, next)}
                            classes={classes}
                            fieldErrors={fieldErrors}
                        />
                    ) : null}

                    {/* Server-side 400 field errors for this specific input.
                        Kept beside each field so operators see exactly which
                        input the message applies to. */}
                    {fieldErrors?.[filter.name] ? (
                        <div className={classes.fieldError}>
                            {fieldErrors[filter.name].join(" · ")}
                        </div>
                    ) : null}
                </div>
            ))}
            {validationError && (
                <div className={classes.errorText}>{validationError}</div>
            )}
        </div>
    );
};

export default FilterForm;
